import {
  ANNOUNCEMENT_AUDIENCES,
  ANNOUNCEMENT_SEVERITIES,
  type AnnouncementSeverity
} from '~/common/api-client';

/** cauldron `Toast` `type` values used for announcement previews/rendering. */
export type AnnouncementToastType = 'info' | 'caution' | 'error';

export const severityToToastType = (
  severity: AnnouncementSeverity
): AnnouncementToastType => {
  switch (severity) {
    case 'warning':
      return 'caution';
    case 'critical':
      return 'error';
    case 'informational':
    default:
      return 'info';
  }
};

// Re-exported under the names the announcement components use; the arrays (and the
// unions derived from them) live next to the API types in api-client.ts.
export const SEVERITIES = ANNOUNCEMENT_SEVERITIES;
export const AUDIENCES = ANNOUNCEMENT_AUDIENCES;

/**
 * Friendly display names for affected-product slugs that aren't in the billing
 * product catalog (so `getProductBySlug` can't resolve them), e.g. the Account
 * Portal pseudo-product. Product names are not translated.
 */
export const PSEUDO_PRODUCT_NAMES: Readonly<Record<string, string>> = {
  'axe-account': 'Axe Account'
};

/**
 * Client-side mirror of the server's announcement-link normalisation
 * (packages/server/src/lib/announcements.ts): accepts URLs with or without a
 * scheme, restricts to http(s), returns null for anything else. Saved links are
 * already normalised server-side; this exists so the form preview never renders
 * an un-vetted value (e.g. a pasted `javascript:` URL) as a live anchor.
 */
export const normalizeAnnouncementLinkForDisplay = (
  value: string | null
): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const isHttpUrl = (candidate: string): boolean => {
    try {
      const { protocol } = new URL(candidate);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  };
  if (isHttpUrl(trimmed)) {
    return trimmed;
  }
  const withScheme = `https://${trimmed}`;
  return isHttpUrl(withScheme) ? withScheme : null;
};

/** ISO datetime → `YYYY-MM-DD` for a native date input. */
export const toDateInputValue = (iso: string): string =>
  iso ? iso.slice(0, 10) : '';

/** `YYYY-MM-DD` (start) → start-of-day ISO. */
export const startDateToIso = (date: string): string => `${date}T00:00:00.000Z`;

/** `YYYY-MM-DD` (end) → end-of-day ISO so a same-day window is valid and inclusive. */
export const endDateToIso = (date: string): string => `${date}T23:59:59.999Z`;
